// @section 1 Atlas Hopper — operator view of the Account Signals free-lead product.
//
// Exists to close a hole: when someone signs up targeting a vertical we cannot
// auto-fill, the product promises "we are building your list by hand" and then
// nothing happens. Nobody learns they exist. This is the queue that makes that
// promise real, plus the signup log.
//
// Data: GET /api/hopper/ops/accounts on api.optiq-os.com (AnyAuth, operator
// identity — deliberately NOT the customer's X-Hopper-Token, which would only
// ever see its own account).
//
// Product surfaces this watches: scalable-os.com/hopper (Account Signals) and
// scalable-os.com/sample (Target Data).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { authFetch } from '../lib/authFetch';

const API_BASE = import.meta.env.VITE_HQ_API_BASE ?? 'https://api.optiq-os.com';

// @subsection 1.1 Types
type HopperAccount = {
  accountId: string;
  email: string;
  companyName: string | null;
  sellsWhat: string | null;
  targetVertical: string | null;
  targetState: string | null;
  createdAt: string;
  freeCap: number;
  purchasedCap: number;
  slotsUsed: number;
  outcomesReported: number;
  connectionsMade: number;
  needsManualSourcing: boolean;
};

type OpsResponse = {
  total: number;
  awaitingManualSourcing: number;
  accounts: HopperAccount[];
};

// @subsection 1.2 Screen
export default function Hopper() {
  const [data, setData] = useState<OpsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [onlyQueue, setOnlyQueue] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await authFetch(`${API_BASE}/api/hopper/ops/accounts`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    return onlyQueue ? data.accounts.filter((a) => a.needsManualSourcing) : data.accounts;
  }, [data, onlyQueue]);

  // Engagement is the signal that matters most per account: someone who
  // connected a system or reported an outcome is actually using the thing.
  const engaged = useMemo(
    () => (data?.accounts ?? []).filter((a) => a.connectionsMade > 0 || a.outcomesReported > 0).length,
    [data],
  );

  return (
    <div style={{ padding: '1.5rem', maxWidth: '78rem' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 700, margin: 0 }}>Hopper</h1>
          <p style={{ margin: '0.35rem 0 0', opacity: 0.7, fontSize: '0.9rem' }}>
            Signups from scalable-os.com. Account Signals (/hopper) and Target Data (/sample).
          </p>
        </div>
        <button onClick={() => void load()} disabled={loading} style={btnStyle}>
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && (
        <p style={{ marginTop: '1rem', color: '#be123c' }}>Could not load: {error}</p>
      )}

      {/* @block 1.2.1 The number that means work today */}
      {data && (
        <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem', flexWrap: 'wrap' }}>
          <Stat label="Signups" value={data.total} />
          <Stat
            label="Owed a hand-built list"
            value={data.awaitingManualSourcing}
            emphasis={data.awaitingManualSourcing > 0}
          />
          <Stat label="Engaged" value={engaged} />
        </div>
      )}

      {data && data.awaitingManualSourcing > 0 && (
        <div style={{ marginTop: '1.25rem', padding: '0.9rem 1.1rem', borderRadius: '0.6rem', background: 'rgba(190,18,60,0.08)', border: '1px solid rgba(190,18,60,0.25)' }}>
          <strong>{data.awaitingManualSourcing}</strong> {data.awaitingManualSourcing === 1 ? 'person was' : 'people were'} told
          we are building their list by hand. Until someone actually builds it, that promise is outstanding.
        </div>
      )}

      {data && data.total > 0 && (
        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.45rem', marginTop: '1.25rem', fontSize: '0.9rem', cursor: 'pointer' }}>
          <input type="checkbox" checked={onlyQueue} onChange={(e) => setOnlyQueue(e.target.checked)} />
          Show only the manual-sourcing queue
        </label>
      )}

      {/* @block 1.2.2 The log */}
      {data && data.total === 0 && !loading && (
        <p style={{ marginTop: '2rem', opacity: 0.7 }}>
          No signups yet. Both products are live; this fills as people arrive.
        </p>
      )}

      {rows.length > 0 && (
        <div style={{ marginTop: '1.25rem', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '58rem', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ textAlign: 'left', borderBottom: '2px solid rgba(128,128,128,0.3)' }}>
                <Th>Who</Th>
                <Th>Sells</Th>
                <Th>Targets</Th>
                <Th>Hopper</Th>
                <Th>Connected</Th>
                <Th>Reported</Th>
                <Th>Signed up</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((a) => (
                <tr
                  key={a.accountId}
                  style={{
                    borderBottom: '1px solid rgba(128,128,128,0.18)',
                    background: a.needsManualSourcing ? 'rgba(190,18,60,0.05)' : undefined,
                  }}
                >
                  <Td>
                    <div style={{ fontWeight: 600 }}>{a.email}</div>
                    {a.companyName && <div style={{ opacity: 0.65, fontSize: '0.82rem' }}>{a.companyName}</div>}
                  </Td>
                  <Td>{a.sellsWhat ?? <Dim />}</Td>
                  <Td>
                    {a.targetVertical ?? <Dim />}
                    {a.targetState ? <span style={{ opacity: 0.65 }}> · {a.targetState}</span> : null}
                  </Td>
                  <Td>
                    {a.needsManualSourcing ? (
                      <span style={{ color: '#be123c', fontWeight: 600 }}>Needs sourcing</span>
                    ) : (
                      <span>
                        {a.slotsUsed} / {a.freeCap + a.purchasedCap}
                        {a.purchasedCap > 0 && (
                          <span style={{ color: '#047857', fontWeight: 600 }}> · paid</span>
                        )}
                      </span>
                    )}
                  </Td>
                  <Td>{a.connectionsMade > 0 ? a.connectionsMade : <Dim />}</Td>
                  <Td>{a.outcomesReported > 0 ? a.outcomesReported : <Dim />}</Td>
                  <Td>{new Date(a.createdAt).toLocaleDateString()}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// @subsection 1.3 Bits
const btnStyle: React.CSSProperties = {
  padding: '0.45rem 0.9rem',
  borderRadius: '0.45rem',
  border: '1px solid rgba(128,128,128,0.35)',
  background: 'transparent',
  cursor: 'pointer',
  font: 'inherit',
};

function Stat({ label, value, emphasis }: { label: string; value: number; emphasis?: boolean }) {
  return (
    <div
      style={{
        padding: '0.85rem 1.1rem',
        borderRadius: '0.6rem',
        border: '1px solid rgba(128,128,128,0.25)',
        minWidth: '9rem',
      }}
    >
      <div style={{ fontSize: '1.7rem', fontWeight: 700, color: emphasis ? '#be123c' : undefined }}>{value}</div>
      <div style={{ fontSize: '0.82rem', opacity: 0.7 }}>{label}</div>
    </div>
  );
}

const Th = ({ children }: { children: React.ReactNode }) => (
  <th style={{ padding: '0.55rem 0.7rem 0.55rem 0', fontWeight: 600, opacity: 0.75 }}>{children}</th>
);
const Td = ({ children }: { children: React.ReactNode }) => (
  <td style={{ padding: '0.7rem 0.7rem 0.7rem 0', verticalAlign: 'top' }}>{children}</td>
);
const Dim = () => <span style={{ opacity: 0.35 }}>none</span>;
